import { Card, Grid2, IconButton, Stack, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { IZoneSchedule } from "../../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { useTranslation } from "react-i18next";
import { handlePermChange } from "../../../Partial/PermissionChange.partial";
import { PermissionSelectGroup } from "../../../Partial/PermissionSelectGroup.partial";
import {
  RemoteCompanyPerm,
  RemoteCompanyUnitsPerm,
  RemoteUsersPerm
} from "../../../../../Domain/Types/FloorPlan/Permission.type";
import { generateNewPerms } from "../../PlaceInfoSidebar/DeskPermissionDialogContent/PermissionDialogContent.partial";
import { NewPerms } from "../../../../Views/CreateFloorPlanView/Tools/useFloorplan/useFloorplan";
import { initPerms } from "../../functions/initPerms.function";
import { RemoteCompany } from "../../../../../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../../../../../features/Connections/types/Colleague.type";

type Props = {
  selectedZones: IZoneSchedule[];
  remoteUnits: {
    remoteCompanies: RemoteCompany[] | undefined;
    remoteCompanyUnits: CompanyUnits[] | undefined;
    remoteUsers: Colleague[] | undefined;
  };
  isRemoteUnitsFetching: boolean;
  isEditable: boolean;
  onChangePerm: (zoneIds: number[], perms: NewPerms) => void;
  onClose: () => void;
};

export function ZonePermissionDialogContent({
  selectedZones,
  remoteUnits,
  isRemoteUnitsFetching,
  isEditable,
  onChangePerm,
  onClose
}: Readonly<Props>) {
  const { t } = useTranslation();

  const { initialPermCompanies, initialPermCompanyUnits, initialPermUsers } =
    initPerms(selectedZones);

  return (
    <Card sx={{ p: 2, maxHeight: "70%", overflowY: "visible", width: "25vw" }}>
      <Stack direction={"column"} gap={4}>
        <Grid2 container>
          <Grid2 size={"grow"} />
          <Grid2 size={{ xs: 9 }} sx={{ marginBottom: "20px" }}>
            <Typography align={"center"} lineHeight={2.25}>
              {t("Adjust permissions")}
            </Typography>
          </Grid2>
          <Grid2 size={"grow"} sx={{ textAlign: "right" }}>
            <IconButton
              sx={{ float: "right" }}
              data-testid="btn-group-close"
              onClick={onClose}
              size="large"
            >
              <Close />
            </IconButton>
          </Grid2>
        </Grid2>
        <PermissionSelectGroup
          initialPermCompanies={initialPermCompanies}
          initialPermCompanyUnits={initialPermCompanyUnits}
          initialPermUsers={initialPermUsers}
          type={"zone"}
          isUsingTimeRelatedPerm={!selectedZones.some(zn => zn.inventory?.zoneTypeId === 3)} // exclude conference zone
          handlePermChange={handlePermChange}
          zoneUpdatePerms={(companies, units, users) =>
            handleZoneUpdatePerms(selectedZones, onChangePerm, onClose, companies, units, users)
          }
          remoteUnits={remoteUnits}
          isRemoteUnitsFetching={isRemoteUnitsFetching}
          isEditable={isEditable}
        />
      </Stack>
    </Card>
  );
}

export function handleZoneUpdatePerms(
  selectedZones: IZoneSchedule[],
  onChangePerm: (zoneIds: number[], perms: NewPerms) => void,
  onClose: () => void,
  selectedCompanies: RemoteCompanyPerm[] = [],
  selectedCompanyUnits: RemoteCompanyUnitsPerm[] = [],
  selectedUsers: RemoteUsersPerm[] = []
) {
  const selectedZonesIds = selectedZones.map(z => z.id);
  const newPerms = generateNewPerms({
    selectedCompanies,
    selectedCompanyUnits,
    selectedUsers
  });

  onChangePerm(selectedZonesIds, newPerms);
  onClose();
}
